import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useShouldShowAIAssistant from '../../../../utils/useShouldShowAIAssistant';
import {
  onboardingHints,
  routes
} from '../../../../constants';
import useRouter from '../../../../utils/useRouter';
import { selectUser } from '../../../../reducers/user.selectors';
import { safeLocalStorage } from '../../../../utils/safeStorage';

export enum MukiBudeOnboardingStep {
    READER_HINT = 'reader-hint',
    MEINE_INHALTE_HINT = 'meine-inhalte-hint',
    IMAGE_GENERATOR_HINT = 'image-generator-hint',
}

export const MukiBudeOnboardingStepLocalStorageKey = {
  [MukiBudeOnboardingStep.READER_HINT]: onboardingHints.aiAssistant.readerHint,
  [MukiBudeOnboardingStep.MEINE_INHALTE_HINT]: onboardingHints.aiAssistant.meineInhalteHint,
  [MukiBudeOnboardingStep.IMAGE_GENERATOR_HINT]: onboardingHints.aiAssistant.imageGeneratorHint,
};

export type MukiBudeOnboardingStepConfig = {
  shouldShow: (context: {
    isReaderPage: boolean;
    isAIToolsPage: boolean;
    isHistoryPage: boolean;
    isSearchPage: boolean;
    isImageGeneratorEligible: boolean;
    stagesShownInCurrentSession: Set<MukiBudeOnboardingStep>;
  }) => boolean;
};

export type MukiBudeOnboardingState = Record<MukiBudeOnboardingStep, {
  shouldShow: boolean;
}>;

const shownBefore = (step: MukiBudeOnboardingStep) => {
  return safeLocalStorage.getItem(MukiBudeOnboardingStepLocalStorageKey[step]) === 'true';
};

const stepConfigs: Record<MukiBudeOnboardingStep, MukiBudeOnboardingStepConfig> = {
  [MukiBudeOnboardingStep.READER_HINT]: {
    shouldShow: ({ isReaderPage }) =>
      isReaderPage,
  },
  [MukiBudeOnboardingStep.MEINE_INHALTE_HINT]: {
    shouldShow: ({ isAIToolsPage, isHistoryPage }) =>
      isAIToolsPage || isHistoryPage,
  },
  [MukiBudeOnboardingStep.IMAGE_GENERATOR_HINT]: {
    shouldShow: ({
      isReaderPage, isAIToolsPage, isHistoryPage, isSearchPage, isImageGeneratorEligible,
    }) =>
      isImageGeneratorEligible
      && !isReaderPage
      && !isAIToolsPage
      && !isHistoryPage
      && !isSearchPage,
  },
};

const useMukiOnboarding = () => {
  const { match: { location: { pathname } } } = useRouter();
  const isReaderPage = pathname.includes(`/${routes.reader.root}/`);
  const isAIToolsPage = pathname.includes(`/${routes.aiTools.root}`);
  const isHistoryPage = pathname.includes(`/${routes.aiAssistantHistory.root}`);
  const isSearchPage = pathname.includes(`/${routes.search.root}`);
  const userHasAccess = useShouldShowAIAssistant();
  const user = useSelector(selectUser);
  const isImageGeneratorEligible = !user?.isSchoolUser;
  // Track which stages have been shown in the current session
  const [stagesShownInCurrentSession, setStagesShownInCurrentSession] =
    useState<Set<MukiBudeOnboardingStep>>(new Set());

  const shouldShowStep = useCallback((step: MukiBudeOnboardingStep) => {
    if (shownBefore(step) || !userHasAccess || user?.isAiOnlyUser) return false;

    const config = stepConfigs[step];

    return config.shouldShow({
      isReaderPage,
      isAIToolsPage,
      isHistoryPage,
      isSearchPage,
      isImageGeneratorEligible,
      stagesShownInCurrentSession,
    });
  }, [
    userHasAccess,
    user?.isAiOnlyUser,
    isReaderPage,
    isAIToolsPage,
    isHistoryPage,
    isSearchPage,
    isImageGeneratorEligible,
    stagesShownInCurrentSession,
  ]);

  const [onboardingState, setOnboardingState] = useState<MukiBudeOnboardingState>(() => {
    const initialState: MukiBudeOnboardingState = {} as MukiBudeOnboardingState;

    for (const step of Object.values(MukiBudeOnboardingStep)) {
      initialState[step] = {
        shouldShow: shouldShowStep(step)
      };
    }

    return initialState;
  });

  const concludeOnboardingStep = (step: MukiBudeOnboardingStep) => {
    safeLocalStorage.setItem(MukiBudeOnboardingStepLocalStorageKey[step], 'true');
    setOnboardingState((prev) => ({ ...prev, [step]: { ...prev[step], shouldShow: false } }));

    setStagesShownInCurrentSession(prev => new Set(prev).add(step));
  };

  useEffect(() => {
    const newState: MukiBudeOnboardingState = {} as MukiBudeOnboardingState;
    let hasChanges = false;

    for (const step of Object.values(MukiBudeOnboardingStep)) {
      const currentShouldShow = onboardingState[step]?.shouldShow ?? false;
      const newShouldShow = shouldShowStep(step);

      newState[step] = { shouldShow: newShouldShow };

      if (currentShouldShow !== newShouldShow) {
        hasChanges = true;
      }
    }

    if (hasChanges) {
      setOnboardingState(newState);
    }
  }, [shouldShowStep]);

  return {
    concludeOnboardingStep,
    onboardingState,
    stagesShownInCurrentSession
  };
};

export default useMukiOnboarding;
